import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from '@formspree/react';
import { useTranslation } from '../hooks/useTranslation';
import { CCTVCamera, DomeCCTV, PTZCamera, DVRUnit } from '../components/Illustrations';
import './Order.css';

const Order = () => {
  const { t } = useTranslation();
  const refs = useRef([]);
  const [, submitToFormspree] = useForm("maqdoaob");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [step, setStep] = useState(1); // 1: Select Plan, 2: Customize, 3: Appointment
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    propertyType: '',
    cameras: '',
    features: [],
    appointmentDate: '',
    appointmentTime: '',
    message: '',
    bookAppointment: false,
    acceptTerms: false,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const plans = [
    {
      id: 'basic',
      name: t('order.plans.basic.name'),
      subtitle: t('order.plans.basic.subtitle'),
      icon: <DomeCCTV size={80} />,
      cameras: '2',
      price: t('order.plans.basic.price'),
      features: t('order.plans.basic.features'),
      color: 'var(--navy)',
      popular: false,
    },
    {
      id: 'standard',
      name: t('order.plans.standard.name'),
      subtitle: t('order.plans.standard.subtitle'),
      icon: <CCTVCamera size={80} />,
      cameras: '4',
      price: t('order.plans.standard.price'),
      features: t('order.plans.standard.features'),
      color: 'var(--green)',
      popular: true,
    },
    {
      id: 'premium',
      name: t('order.plans.premium.name'),
      subtitle: t('order.plans.premium.subtitle'),
      icon: <PTZCamera size={80} />,
      cameras: '8',
      price: t('order.plans.premium.price'),
      features: t('order.plans.premium.features'),
      color: 'var(--saffron)',
      popular: false,
    },
    {
      id: 'enterprise',
      name: t('order.plans.enterprise.name'),
      subtitle: t('order.plans.enterprise.subtitle'),
      icon: <DVRUnit size={100} />,
      cameras: '16+',
      price: t('order.plans.enterprise.price'),
      features: t('order.plans.enterprise.features'),
      color: 'var(--navy-deep)',
      popular: false,
    },
  ];

  const propertyTypes = [
    { value: 'home', label: t('order.form.propertyTypes.home') },
    { value: 'shop', label: t('order.form.propertyTypes.shop') },
    { value: 'office', label: t('order.form.propertyTypes.office') },
    { value: 'factory', label: t('order.form.propertyTypes.factory') },
    { value: 'warehouse', label: t('order.form.propertyTypes.warehouse') },
    { value: 'school', label: t('order.form.propertyTypes.school') },
    { value: 'hospital', label: t('order.form.propertyTypes.hospital') },
    { value: 'other', label: t('order.form.propertyTypes.other') },
  ];

  const additionalFeatures = [
    { id: 'nightVision', label: t('order.form.features.nightVision') },
    { id: 'remoteViewing', label: t('order.form.features.remoteViewing') },
    { id: 'motionDetection', label: t('order.form.features.motionDetection') },
    { id: 'audioRecording', label: t('order.form.features.audioRecording') },
    { id: 'cloudStorage', label: t('order.form.features.cloudStorage') },
    { id: 'ptzControl', label: t('order.form.features.ptzControl') },
  ];

  const timeSlots = [
    '09:00 AM - 11:00 AM',
    '11:00 AM - 01:00 PM',
    '02:00 PM - 04:00 PM',
    '04:00 PM - 06:00 PM',
  ];

  useEffect(() => {
    let observer;
    // Small delay to ensure new elements are rendered and added to refs
    const timer = setTimeout(() => {
      observer = new IntersectionObserver(
        entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
        { threshold: 0.06 }
      );
      refs.current.forEach(el => el && observer.observe(el));
    }, 100);
    return () => {
      clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, [step]); // Re-observe when step changes

  const add = el => { if (el && !refs.current.includes(el)) refs.current.push(el); };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setFormData(prev => ({ ...prev, cameras: plan.cameras }));
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'features') {
      setFormData(prev => ({
        ...prev,
        features: checked
          ? [...prev.features, value]
          : prev.features.filter(f => f !== value),
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('order.form.errors.nameRequired');
    if (!formData.phone.trim()) newErrors.phone = t('order.form.errors.phoneRequired');
    else if (!/^\d{10,12}$/.test(formData.phone.replace(/[\s\-+]/g, ''))) {
      newErrors.phone = t('order.form.errors.phoneInvalid');
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('order.form.errors.emailInvalid');
    }
    if (!formData.location.trim()) newErrors.location = t('order.form.errors.locationRequired');
    if (!formData.propertyType) newErrors.propertyType = t('order.form.errors.propertyRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinueToAppointment = () => {
    if (validateStep2()) {
      setStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate terms acceptance
    if (!formData.acceptTerms) {
      setErrors(prev => ({ ...prev, acceptTerms: t('terms.checkbox.error') }));
      return;
    }
    
    setIsSubmitting(true);
    
    // Build the order data for Formspree
    const hasDiscount = formData.bookAppointment && formData.appointmentDate && formData.appointmentTime;
    const featuresText = formData.features.length > 0 ? formData.features.join(', ') : 'None';
    
    // Create FormData for Formspree submission
    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('phone', formData.phone);
    submitData.append('email', formData.email || 'Not provided');
    submitData.append('location', formData.location);
    submitData.append('propertyType', formData.propertyType);
    submitData.append('serviceRequired', 'CCTV Order Request');
    submitData.append('message', `
=== CCTV ORDER REQUEST ===
Plan: ${selectedPlan?.name || 'Custom Configuration'}
Cameras: ${formData.cameras}
Property Type: ${formData.propertyType}
Location: ${formData.location}
Additional Features: ${featuresText}
${hasDiscount ? `\n🎉 APPOINTMENT BOOKED (10% Discount)\nDate: ${formData.appointmentDate}\nTime: ${formData.appointmentTime}` : ''}
${formData.message ? `\nAdditional Notes: ${formData.message}` : ''}
========================
    `.trim());
    
    try {
      await submitToFormspree(submitData);
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Form submission error:', err);
    }
    setIsSubmitting(false);
  };

  const goBack = () => {
    setStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Success Screen
  if (isSubmitted) {
    return (
      <div className="order-success">
        <div className="container">
          <div className="order-success__card">
            <div className="order-success__icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="8 12 11 15 16 9" />
              </svg>
            </div>
            <h1>{t('order.success.title')}</h1>
            <p>{t('order.success.message')}</p>
            
            <div className="order-success__summary">
              <h3>{t('order.success.summaryTitle')}</h3>
              <div className="summary-item">
                <span>{t('order.success.plan')}:</span>
                <strong>{selectedPlan?.name || 'Custom'}</strong>
              </div>
              <div className="summary-item">
                <span>{t('order.success.cameras')}:</span>
                <strong>{formData.cameras}</strong>
              </div>
              <div className="summary-item">
                <span>{t('order.success.location')}:</span>
                <strong>{formData.location}</strong>
              </div>
              {formData.bookAppointment && formData.appointmentDate && (
                <>
                  <div className="summary-item highlight">
                    <span>{t('order.success.appointment')}:</span>
                    <strong>{formData.appointmentDate} ({formData.appointmentTime})</strong>
                  </div>
                  <div className="discount-badge">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                      <line x1="7" y1="7" x2="7.01" y2="7"/>
                    </svg>
                    {t('order.success.discountApplied')}
                  </div>
                </>
              )}
            </div>
            
            <p className="order-success__note">{t('order.success.note')}</p>
            
            <div className="order-success__actions">
              <a href="tel:+919421722575" className="btn btn-green">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                {t('order.success.callNow')}
              </a>
              <a href="https://wa.me/919421722575" target="_blank" rel="noopener noreferrer" className="btn btn-outline">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-page">
      {/* Hero */}
      <section className="page-hero order-hero">
        <div className="container page-hero__inner">
          <div className="reveal" ref={add}>
            <div className="section-badge">{t('order.hero.badge')}</div>
            <div className="vocal-local-badge">
              <span>🇮🇳</span>
              <span className="vocal-text">{t('home.hero.vocalForLocal') || 'Vocal for Local'}</span>
              <span className="vocal-divider">|</span>
              <span className="vocal-sub">{t('home.hero.kolhapursOwn') || "Kolhapur's Own"}</span>
            </div>
            <h1>{t('order.hero.title1')}<br /><em style={{ fontStyle: 'italic', color: 'var(--green)' }}>{t('order.hero.title2')}</em></h1>
            <p style={{ color: 'var(--text-mid)', fontSize: '1.05rem', lineHeight: '1.75', maxWidth: '520px', marginTop: '1rem' }}>
              {t('order.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Progress Steps */}
      <div className="order-progress">
        <div className="container">
          <div className="progress-steps">
            <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
              <div className="step-number">{step > 1 ? '✓' : '1'}</div>
              <span>{t('order.steps.selectPlan')}</span>
            </div>
            <div className="progress-line"></div>
            <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
              <div className="step-number">{step > 2 ? '✓' : '2'}</div>
              <span>{t('order.steps.customize')}</span>
            </div>
            <div className="progress-line"></div>
            <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <span>{t('order.steps.confirm')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Step 1: Select Plan */}
      {step === 1 && (
        <section className="section-padding" style={{ background: 'var(--off-white)' }}>
          <div className="container">
            <div className="text-center reveal" ref={add} style={{ marginBottom: '3rem' }}>
              <h2>{t('order.selectPlan.title')}</h2>
              <p style={{ color: 'var(--text-mid)', marginTop: '0.5rem' }}>{t('order.selectPlan.subtitle')}</p>
            </div>

            <div className="plans-grid">
              {plans.map((plan, i) => (
                <div
                  key={plan.id}
                  className={`plan-card reveal ${plan.popular ? 'plan-card--popular' : ''}`}
                  ref={add}
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  {plan.popular && <div className="popular-badge">{t('order.plans.popularBadge')}</div>}
                  <div className="plan-icon" style={{ borderColor: plan.color }}>
                    {plan.icon}
                  </div>
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-subtitle">{plan.subtitle}</p>
                  <div className="plan-cameras">
                    <span className="cameras-count">{plan.cameras}</span>
                    <span className="cameras-label">{t('order.plans.cameras')}</span>
                  </div>
                  <div className="plan-price">{plan.price}</div>
                  <ul className="plan-features">
                    {Array.isArray(plan.features) && plan.features.map((f, idx) => (
                      <li key={idx}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    className={`btn ${plan.popular ? 'btn-green' : 'btn-outline'}`}
                    onClick={() => handlePlanSelect(plan)}
                  >
                    {t('order.plans.selectButton')}
                  </button>
                </div>
              ))}
            </div>

            <div className="custom-option reveal" ref={add}>
              <p>{t('order.customOption.text')}</p>
              <button className="btn btn-saffron" onClick={() => { setSelectedPlan(null); setStep(2); }}>
                {t('order.customOption.button')}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Step 2: Customize Requirements */}
      {step === 2 && (
        <section className="section-padding" style={{ background: 'var(--off-white)' }}>
          <div className="container">
            <button className="back-button" onClick={goBack}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              {t('order.form.backButton')}
            </button>

            {selectedPlan && (
              <div className="selected-plan-banner reveal" ref={add}>
                <div className="selected-plan__icon">{selectedPlan.icon}</div>
                <div className="selected-plan__info">
                  <h3>{selectedPlan.name}</h3>
                  <p>{selectedPlan.cameras} {t('order.plans.cameras')} • {selectedPlan.price}</p>
                </div>
                <button className="change-plan-btn" onClick={() => setStep(1)}>{t('order.form.changePlan')}</button>
              </div>
            )}

            <div className="order-form-container reveal" ref={add}>
              <h2>{t('order.form.title')}</h2>
              <p className="form-subtitle">{t('order.form.subtitle')}</p>

              <form className="order-form">
                <div className="form-row">
                  <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
                    <label>{t('order.form.name')} *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('order.form.namePlaceholder')}
                    />
                    {errors.name && <span className="error-text">{errors.name}</span>}
                  </div>
                  <div className={`form-group ${errors.phone ? 'has-error' : ''}`}>
                    <label>{t('order.form.phone')} *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder={t('order.form.phonePlaceholder')}
                    />
                    {errors.phone && <span className="error-text">{errors.phone}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
                    <label>{t('order.form.email')}</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('order.form.emailPlaceholder')}
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>
                  <div className={`form-group ${errors.location ? 'has-error' : ''}`}>
                    <label>{t('order.form.location')} *</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder={t('order.form.locationPlaceholder')}
                    />
                    {errors.location && <span className="error-text">{errors.location}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className={`form-group ${errors.propertyType ? 'has-error' : ''}`}>
                    <label>{t('order.form.propertyType')} *</label>
                    <select
                      name="propertyType"
                      value={formData.propertyType}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('order.form.selectPropertyType')}</option>
                      {propertyTypes.map(pt => (
                        <option key={pt.value} value={pt.value}>{pt.label}</option>
                      ))}
                    </select>
                    {errors.propertyType && <span className="error-text">{errors.propertyType}</span>}
                  </div>
                  <div className="form-group">
                    <label>{t('order.form.cameras')}</label>
                    <select
                      name="cameras"
                      value={formData.cameras}
                      onChange={handleInputChange}
                    >
                      <option value="2">2 {t('order.plans.cameras')}</option>
                      <option value="4">4 {t('order.plans.cameras')}</option>
                      <option value="6">6 {t('order.plans.cameras')}</option>
                      <option value="8">8 {t('order.plans.cameras')}</option>
                      <option value="12">12 {t('order.plans.cameras')}</option>
                      <option value="16">16 {t('order.plans.cameras')}</option>
                      <option value="16+">16+ {t('order.plans.cameras')}</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('order.form.additionalFeatures')}</label>
                  <div className="features-grid">
                    {additionalFeatures.map(feature => (
                      <label key={feature.id} className="feature-checkbox">
                        <input
                          type="checkbox"
                          name="features"
                          value={feature.id}
                          checked={formData.features.includes(feature.id)}
                          onChange={handleInputChange}
                        />
                        <span className="checkmark"></span>
                        {feature.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('order.form.message')}</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={t('order.form.messagePlaceholder')}
                    rows="4"
                  ></textarea>
                </div>

                <button type="button" className="btn btn-green btn-lg" onClick={handleContinueToAppointment}>
                  {t('order.form.continueButton')}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Step 3: Appointment & Confirm */}
      {step === 3 && (
        <section className="section-padding" style={{ background: 'var(--off-white)' }}>
          <div className="container">
            <button className="back-button" onClick={goBack}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              {t('order.form.backButton')}
            </button>

            <div className="appointment-section reveal" ref={add}>
              {/* Discount Banner */}
              <div className="discount-banner">
                <div className="discount-banner__icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                    <line x1="9" y1="9" x2="9.01" y2="9" />
                    <line x1="15" y1="9" x2="15.01" y2="9" />
                  </svg>
                </div>
                <div className="discount-banner__content">
                  <h3>{t('order.appointment.discountTitle')}</h3>
                  <p>{t('order.appointment.discountSubtitle')}</p>
                </div>
                <div className="discount-banner__badge">
                  <span className="discount-percent">10%</span>
                  <span className="discount-off">{t('order.appointment.off')}</span>
                </div>
              </div>

              <div className="order-form-container">
                <h2>{t('order.appointment.title')}</h2>
                <p className="form-subtitle">{t('order.appointment.subtitle')}</p>

                <form className="order-form" onSubmit={handleSubmit}>
                  <div className="appointment-toggle">
                    <label className="toggle-checkbox">
                      <input
                        type="checkbox"
                        name="bookAppointment"
                        checked={formData.bookAppointment}
                        onChange={handleInputChange}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-label">
                        {t('order.appointment.bookSiteVisit')}
                        <span className="discount-tag">10% OFF</span>
                      </span>
                    </label>
                  </div>

                  {formData.bookAppointment && (
                    <div className="appointment-fields">
                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('order.appointment.selectDate')} *</label>
                          <input
                            type="date"
                            name="appointmentDate"
                            value={formData.appointmentDate}
                            onChange={handleInputChange}
                            min={getMinDate()}
                            required={formData.bookAppointment}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('order.appointment.selectTime')} *</label>
                          <select
                            name="appointmentTime"
                            value={formData.appointmentTime}
                            onChange={handleInputChange}
                            required={formData.bookAppointment}
                          >
                            <option value="">{t('order.appointment.selectTimeSlot')}</option>
                            {timeSlots.map(slot => (
                              <option key={slot} value={slot}>{slot}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Terms Checkbox */}
                  <div className="terms-checkbox-container">
                    <label className={`terms-checkbox-label ${errors.acceptTerms ? 'has-error' : ''}`}>
                      <input
                        type="checkbox"
                        name="acceptTerms"
                        checked={formData.acceptTerms}
                        onChange={handleInputChange}
                      />
                      <span className="checkmark"></span>
                      <span className="terms-text">
                        {t('terms.checkbox.text')} <Link to="/terms" target="_blank">{t('terms.checkbox.link')}</Link>
                      </span>
                    </label>
                    {errors.acceptTerms && <span className="error-message">{errors.acceptTerms}</span>}
                  </div>

                  {/* Order Summary */}
                  <div className="order-summary">
                    <h3>{t('order.summary.title')}</h3>
                    <div className="summary-row">
                      <span>{t('order.summary.plan')}:</span>
                      <strong>{selectedPlan?.name || t('order.summary.customPlan')}</strong>
                    </div>
                    <div className="summary-row">
                      <span>{t('order.summary.cameras')}:</span>
                      <strong>{formData.cameras}</strong>
                    </div>
                    <div className="summary-row">
                      <span>{t('order.summary.property')}:</span>
                      <strong>{propertyTypes.find(p => p.value === formData.propertyType)?.label}</strong>
                    </div>
                    <div className="summary-row">
                      <span>{t('order.summary.location')}:</span>
                      <strong>{formData.location}</strong>
                    </div>
                    {formData.features.length > 0 && (
                      <div className="summary-row">
                        <span>{t('order.summary.features')}:</span>
                        <strong>{formData.features.length} {t('order.summary.selected')}</strong>
                      </div>
                    )}
                    {formData.bookAppointment && formData.appointmentDate && (
                      <div className="summary-row highlight">
                        <span>{t('order.summary.discount')}:</span>
                        <strong className="discount-text">-10% {t('order.appointment.off')}</strong>
                      </div>
                    )}
                  </div>

                  <button type="submit" className="btn btn-green btn-lg btn-submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : t('order.form.submitButton')}
                    {!isSubmitting && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>

                  <p className="form-note">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    {t('order.form.privacyNote')}
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Order;
